import type {
  ComponentPropsWithRef,
  ElementType,
  ReactElement,
  ReactNode,
} from "react";
import { type DropdownProps } from "../Dropdown";

export type MenuOption = {
  id: string;
  text: string;
  icon?: ReactNode;
  accessory?: ReactNode;
  tooltip?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  options?: MenuItem[];
  className?: string;
  dataAttributes?: Record<string, string>;
};

export type MenuDivider = {
  type: "divider";
  className?: string;
};

export type MenuHeader = {
  type: "header";
  text: string;
  className?: string;
};

export type MenuCustomElement = {
  type: "element";
  element: ReactElement;
};

export type MenuItem =
  | MenuOption
  | MenuDivider
  | MenuHeader
  | MenuCustomElement;

export type MenuProps = Omit<
  DropdownProps,
  "interactions" | "role" | "trigger" | "children" | "content"
> & {
  trigger?: ReactElement<ComponentPropsWithRef<ElementType>>;
  children: ReactElement<ComponentPropsWithRef<ElementType>>;
  options: MenuItem[];
  onSelect: (id: string) => void;
  preventDefaultOnTrigger?: DropdownProps["preventDefaultOnTrigger"];
  stopPropagationOnTrigger?: DropdownProps["stopPropagationOnTrigger"];
};

export const isMenuOption = (item: MenuItem): item is MenuOption => {
  return !("type" in item);
};

export const isMenuDivider = (item: MenuItem): item is MenuDivider => {
  return "type" in item && item.type === "divider";
};

export const isMenuHeader = (item: MenuItem): item is MenuHeader => {
  return "type" in item && item.type === "header";
};

export const isMenuCustomElement = (
  item: MenuItem,
): item is MenuCustomElement => {
  return "type" in item && item.type === "element";
};
